/**
 * What the account's Plus subscription is, as the API states it.
 *
 * The plan and the subscription are separate fields on purpose: an account can
 * be Plus with no subscription — by invitation, or through a centre seat — and
 * deriving one from the other would offer a Cancel button for something nobody
 * is being charged for.
 */

export type SubscriptionPlan = "basic" | "plus";

/**
 * Closed on purpose. An unhandled state is then a type error in the component
 * that switches on it, rather than a card that renders nothing.
 */
export type PlusSubscriptionStatus =
    | "incomplete"
    | "active"
    | "past_due"
    | "canceled";

export type BillingPeriod = "monthly" | "annual";

/**
 * Plus or Max: the same plan at two sizes. They hold the same tools and the
 * same paid modules at the same prices; Max carries 100 sessions a month
 * instead of 60. The account's plan stays "plus" for both.
 */
export type PlanTier = "plus" | "max";

export const toTier = (raw: string | undefined | null): PlanTier =>
    raw === "max" ? "max" : "plus";

/** Paying for a year at once costs this much less. Mirrors the backend. */
export const ANNUAL_DISCOUNT_PERCENT = 10;

export interface QuoteLine {
    key: string;
    label: string;
    /** Minor units, for one unit and one billing period. */
    unitCents: number;
    quantity: number;
    totalCents: number;
}

export interface Quote {
    period: BillingPeriod;
    lines: QuoteLine[];
    totalCents: number;
    /** What it works out at per month — the line shown under an annual figure. */
    monthlyEquivalentCents: number;
    /** What paying annually saves. Zero monthly, and zero when nothing is charged. */
    discountCents: number;
    /** The code applied to this quote, if one was. */
    couponCode?: string | null;
    /** What the provider calls it — its own words, never ours. */
    couponDescribes?: string | null;
    /**
     * What the code leaves to pay, as the server worked it out from these
     * very lines. **Null means it could not be stated**: a coupon tied to
     * particular prices, or one whose minimum this purchase has not reached.
     * The screen then says the discount lands at payment rather than showing
     * a figure nobody can stand behind.
     */
    couponTotalCents?: number | null;
}

/**
 * A discount code, in the provider's own words.
 *
 * `describes` is the coupon's name at the provider — "Primer año al 20 %" —
 * and it is what the screen shows. What a discount comes to on a particular
 * invoice depends on rules that live with the money: which prices it covers,
 * for how many invoices, with what minimum. Working that out here would be a
 * second opinion about somebody's bill, so the total stays undiscounted until
 * the provider has applied it.
 */
export interface Coupon {
    code: string;
    describes: string;
    percentOff: number | null;
    amountOffCents: number | null;
    currency: string | null;
}

export interface CouponDTO {
    code: string;
    describes: string;
    percent_off?: number | null;
    amount_off_cents?: number | null;
    currency?: string | null;
}

export const toCoupon = (dto: CouponDTO): Coupon => ({
    code: dto.code,
    describes: dto.describes,
    percentOff: dto.percent_off ?? null,
    amountOffCents: dto.amount_off_cents ?? null,
    currency: dto.currency ?? null,
});

export interface PlusPrice {
    /** Minor units, as the provider states it. Formatted in the browser, which knows the locale. */
    amount: number;
    currency: string;
    interval: string;
}

export interface PlusSubscriptionDetail {
    status: PlusSubscriptionStatus;
    billingPeriod: BillingPeriod;
    /** The size that renews. */
    tier: PlanTier;
    /** The size held now: Max given up mid-period is kept until `maxUntil`. */
    heldTier: PlanTier;
    maxUntil: Date | null;
    /** When the paid period runs out: the next charge, or the day Plus stops. */
    currentPeriodEnd: Date | null;
    cancelAtPeriodEnd: boolean;
    /** The free trial running now: nothing charged yet, the first charge on `trialEndsAt`. */
    inTrial: boolean;
    trialEndsAt: Date | null;
    /** The next charge is due within a month, to a card that will have expired. */
    cardExpiring: boolean;
}

/** Why somebody is leaving, in the provider's vocabulary. */
export const CANCELLATION_REASONS = [
    "too_expensive",
    "missing_features",
    "switched_service",
    "unused",
    "too_complex",
    "low_quality",
    "customer_service",
    "other",
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

/** A paid module that can be bought alongside the plan. */
export interface ModuleOption {
    slug: string;
    /** The catalogue's id, for the endpoints that take one — a centre's tool policy. */
    toolId: string | null;
    /** The catalogue's own name, used only where this app has no copy of its own. */
    name: string;
    monthlyPriceCents: number;
    /** The same module over a year, the annual discount already applied. */
    annualPriceCents: number;
    /** Already theirs: shown ticked and fixed, so the total is the whole bill. */
    adopted: boolean;
}

export interface PlusSubscriptionState {
    plan: SubscriptionPlan;
    /**
     * This account has Plus free for ever, so there is nothing to buy and
     * nothing to cancel. Separate from `plan`: the plan says what the account
     * may do, this says whether anybody is billed for it.
     */
    earlyAdopter: boolean;
    /** False where Plus cannot be bought at all; the button is disabled and says why. */
    available: boolean;
    price: PlusPrice | null;
    /** One per period, so both sides of the choice render without a second request. */
    quotes: Quote[];
    /** Both sizes on both periods, so Plus and Max can be offered side by side. */
    plusQuotes: Quote[];
    maxQuotes: Quote[];
    /** What can be added to the purchase. Empty where none is sold. */
    modules: ModuleOption[];
    /** The free trial the next checkout starts with, in days; 0 when it has been had. */
    trialDays: number;
    subscription: PlusSubscriptionDetail | null;
}

/** The wire shape, snake_case as the API sends it. */
export interface PlusSubscriptionStateDTO {
    plan: string;
    early_adopter?: boolean;
    available: boolean;
    price: { amount: number; currency: string; interval: string } | null;
    quotes?: {
        period: string;
        lines: {
            key: string;
            label: string;
            unit_cents: number;
            quantity: number;
            total_cents: number;
        }[];
        total_cents: number;
        monthly_equivalent_cents: number;
        discount_cents: number;
        coupon_code?: string | null;
        coupon_describes?: string | null;
        coupon_total_cents?: number | null;
    }[];
    plus_quotes?: NonNullable<PlusSubscriptionStateDTO["quotes"]>;
    max_quotes?: NonNullable<PlusSubscriptionStateDTO["quotes"]>;
    modules?: {
        slug: string;
        name: string;
        tool_id?: string | null;
        monthly_price_cents: number;
        annual_price_cents?: number;
        adopted?: boolean;
    }[];
    trial_days?: number;
    subscription: {
        status: string;
        billing_period?: string;
        tier?: string;
        held_tier?: string;
        max_until?: string | null;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
        in_trial?: boolean;
        trial_ends_at?: string | null;
        card_expiring?: boolean;
        card_expires_at?: string | null;
    } | null;
}

const KNOWN_STATUSES: PlusSubscriptionStatus[] = [
    "incomplete",
    "active",
    "past_due",
    "canceled",
];

/**
 * A status the client does not know grants nothing.
 *
 * The same rule the backend applies when reading one: never treat an unknown
 * word as "paid".
 */
const toStatus = (raw: string): PlusSubscriptionStatus =>
    (KNOWN_STATUSES as string[]).includes(raw)
        ? (raw as PlusSubscriptionStatus)
        : "incomplete";

/** A period the client does not know reads as monthly — never as the longer
 *  commitment, which would show a year's price under a month's label. */
const toPeriod = (raw: string | undefined): BillingPeriod =>
    raw === "annual" ? "annual" : "monthly";

const toDate = (raw: string | null): Date | null => {
    if (!raw) return null;
    const parsed = new Date(raw);
    // An unparseable date is no date. Rendering "Invalid Date" as the day
    // somebody loses Plus would be worse than saying nothing.
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export type QuoteDTO = NonNullable<PlusSubscriptionStateDTO["quotes"]>[number];
type SubscriptionDetailDTO = NonNullable<PlusSubscriptionStateDTO["subscription"]>;

/** Shared with the centre's state, which carries the same quote shape. */
export const toQuote = (quote: QuoteDTO): Quote => ({
    period: toPeriod(quote.period),
    lines: quote.lines.map((line) => ({
        key: line.key,
        label: line.label,
        unitCents: line.unit_cents,
        quantity: line.quantity,
        totalCents: line.total_cents,
    })),
    totalCents: quote.total_cents,
    monthlyEquivalentCents: quote.monthly_equivalent_cents,
    discountCents: quote.discount_cents,
    couponCode: quote.coupon_code ?? null,
    couponDescribes: quote.coupon_describes ?? null,
    couponTotalCents: quote.coupon_total_cents ?? null,
});

/** What is actually going to be charged, discount included where it is known. */
export const payableCents = (quote: Quote): number =>
    quote.couponTotalCents ?? quote.totalCents;

export const toSubscriptionDetail = (
    detail: SubscriptionDetailDTO
): PlusSubscriptionDetail => ({
    status: toStatus(detail.status),
    billingPeriod: toPeriod(detail.billing_period),
    tier: toTier(detail.tier),
    heldTier: toTier(detail.held_tier ?? detail.tier),
    maxUntil: toDate(detail.max_until ?? null),
    currentPeriodEnd: toDate(detail.current_period_end),
    cancelAtPeriodEnd: Boolean(detail.cancel_at_period_end),
    inTrial: Boolean(detail.in_trial),
    trialEndsAt: toDate(detail.trial_ends_at ?? null),
    cardExpiring: Boolean(detail.card_expiring),
});

export const toPlusSubscriptionState = (
    dto: PlusSubscriptionStateDTO
): PlusSubscriptionState => ({
    plan: dto.plan === "plus" ? "plus" : "basic",
    earlyAdopter: Boolean(dto.early_adopter),
    available: Boolean(dto.available),
    price: dto.price ?? null,
    quotes: (dto.quotes ?? []).map(toQuote),
    plusQuotes: (dto.plus_quotes ?? dto.quotes ?? []).map(toQuote),
    maxQuotes: (dto.max_quotes ?? []).map(toQuote),
    modules: (dto.modules ?? []).map((module) => ({
        slug: module.slug,
        name: module.name,
        toolId: module.tool_id ?? null,
        monthlyPriceCents: module.monthly_price_cents,
        annualPriceCents: module.annual_price_cents ?? 0,
        adopted: Boolean(module.adopted),
    })),
    trialDays: Math.max(0, Number(dto.trial_days ?? 0) || 0),
    subscription: dto.subscription ? toSubscriptionDetail(dto.subscription) : null,
});

/** The modules somebody could still tick: the ones they do not hold yet. */
export const addableModules = (state: PlusSubscriptionState): ModuleOption[] =>
    state.modules.filter((module) => !module.adopted);

/**
 * What a module costs over the period being bought.
 *
 * Picked from the two figures the server sent, never worked out from one of
 * them: the annual discount is applied and rounded in `pricing.py`, and a
 * second version of that sum here would disagree with the invoice by a cent
 * on some price nobody has tried yet.
 */
export const modulePriceFor = (module: ModuleOption, period: BillingPeriod): number =>
    period === "annual" ? module.annualPriceCents : module.monthlyPriceCents;

/** The quote for one period, or null when the server sent none. */
/**
 * The quote for a period. With a `tier`, the one at that size; without, the
 * size that renews (Plus for somebody who has not bought anything).
 */
export const quoteFor = (
    state: PlusSubscriptionState,
    period: BillingPeriod,
    tier?: PlanTier
): Quote | null => {
    const quotes =
        tier === "max" ? state.maxQuotes : tier === "plus" ? state.plusQuotes : state.quotes;
    return quotes.find((quote) => quote.period === period) ?? null;
};

/**
 * The size this account holds right now. An early adopter holds Max; Plus
 * with no subscription behind it — by invitation — is the Plus size.
 */
export const heldTier = (state: PlusSubscriptionState): PlanTier =>
    state.earlyAdopter ? "max" : state.subscription?.heldTier ?? "plus";

/** Whether the account may buy Plus right now. */
export const canSubscribe = (state: PlusSubscriptionState): boolean =>
    // An early adopter is excluded first: they hold Plus for ever, so a
    // purchase would sell them what they already have. The server refuses it
    // too — this only keeps the button from being offered.
    !state.earlyAdopter &&
    state.available &&
    state.subscription === null &&
    state.plan !== "plus";

/** The day a trial started today would first charge, or null with no trial on offer. */
export const trialChargeDate = (trialDays: number, now: Date = new Date()): Date | null => {
    if (trialDays <= 0) return null;
    const date = new Date(now);
    date.setDate(date.getDate() + trialDays);
    return date;
};

/** Whether a pending cancellation is waiting to be undone. */
export const isCancelling = (state: PlusSubscriptionState): boolean =>
    Boolean(state.subscription?.cancelAtPeriodEnd) &&
    state.subscription?.status !== "canceled";

/** The keys a quote line can carry that are not a tool's slug. */
export const PLUS_LINE = "plus";
export const SEAT_LINE = "seat";
export const MAX_LINE = "max";
export const MAX_SEAT_LINE = "seat-max";
